import Link from "next/link";
import { useRouter } from "next/router";
import useSWR from "swr";

import { OverlayTrigger, Tooltip } from "react-bootstrap";

import getUnixTime from "date-fns/getUnixTime";
import startOfDay from "date-fns/startOfDay";
import { twMerge } from "tailwind-merge";

import { faCode } from "@fortawesome/free-solid-svg-icons/faCode";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons/faExternalLinkAlt";

import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Iframe from "components/Iframe";
import useNotification from "components/Notification/useNotification";
import useActivityTracker from "hooks/useActivityTracker";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

import styles from "./style.module.scss";

type EntityRecentEventProps = {
  slug?: string;
  widget?: boolean;
  containerClassName?: string;
  headerClassName?: string;
};

const fetcher = async (url: string) =>
  baseFetcher(url + `&current-time=${getUnixTime(startOfDay(new Date()))}`);

export const entityRecentEventsUrl = (slug: string, source: string) => {
  const url = new ApiEndpoint(`/api/v2/entities/${slug}/corporate-events`);
  url.searchParams.append("page[size]", "1");
  url.searchParams.append("page[number]", "1");
  url.searchParams.append("filter[source]", source);
  url.searchParams.append("filter[source_url]", "true");
  url.searchParams.append("sort", "future-and-past");

  return url.href;
};

const EntityRecentEvent = ({
  slug: inputSlug,
  widget,
  containerClassName,
  headerClassName,
}: EntityRecentEventProps) => {
  const router = useRouter();
  const slug = inputSlug || (router.query.slug as string);

  const { setNotification } = useNotification();
  const { trackActivity } = useActivityTracker();

  const { data } = useSWR(entityRecentEventsUrl(slug, "quartr"), fetcher);

  if (!data?.data?.length) return <></>;

  const event = data.data.find((event: any) => !!event.sourceUrl);

  if (!event) return <></>;

  const {
    quartrEventUrls: { webembedPath },
  } = event;

  const content = (
    <div className={twMerge(styles.container)}>
      <div className={twMerge(styles.title, headerClassName)}>Latest Event</div>
      <div className="p-[15px] relative">
        <Iframe
          src={webembedPath}
          className="w-full h-[250px]"
          onInferredClick={() => trackActivity("quartr_iframe.click", event)}
        />
        <OverlayTrigger
          placement="top"
          trigger={["hover", "focus"]}
          overlay={<Tooltip>Copy Embed code</Tooltip>}
        >
          <div className={twMerge("absolute", "top-[20px] right-[60px]")}>
            <FontAwesomeIcon
              icon={faCode}
              className={twMerge(
                "text-[18px] text-smart-teal hover:text-smart-teal",
                "cursor-pointer"
              )}
              onClick={() => {
                navigator.clipboard.writeText(
                  `<iframe src="${webembedPath}" style='width:685px;height:180px'></iframe>`
                );
                setNotification("Copied to clipboard successfully.", "success");
              }}
            />
          </div>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          trigger={["hover", "focus"]}
          overlay={<Tooltip>Open in New Tab</Tooltip>}
        >
          <div className={twMerge("absolute", "top-[20px] right-[30px]")}>
            <FontAwesomeIcon
              icon={faExternalLinkAlt}
              className={twMerge(
                "text-smart-teal hover:text-smart-teal",
                "cursor-pointer"
              )}
              onClick={() => window.open(webembedPath)}
            />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );

  if (widget) {
    return (
      <CustomCard className={twMerge(styles.card, containerClassName)}>
        {content}
        <div className="px-[15px] pb-[15px]">
          <Link
            href={`/entities/${slug}/announcements?tab=events`}
            className="text-[14px] font-bold text-smart-teal hover:text-dark-teal"
          >
            {"View more Events »"}
          </Link>
        </div>
      </CustomCard>
    );
  }

  return content;
};

export default EntityRecentEvent;
